import React, { useState } from 'react';

/**
 * EngineDiagnostics — collapsible panel that surfaces the engine's audit
 * trail per route segment. Useful for power users, the dev team, and the
 * eventual telemetry-based calibration loop.
 *
 * Each segment exposes:
 *   - Drive cycle multiplier (speed-based + locale factor, Heliyon-anchored)
 *   - Tire pressure penalty (v2 if context provided, else v1)
 *   - Drivetrain efficiency (accounts for decoupled rear axle)
 *   - Road quality multiplier
 *   - Ambient temperature, wind speed/direction, gradient
 *
 * Hidden by default to keep the main Result page focused on the headline:
 * arrival SoC, distance, time, charging.
 */
export default function EngineDiagnostics({ socTrace }) {
  const [open, setOpen] = useState(false);

  if (!socTrace?.points?.length) return null;
  // If no point has diagnostics, skip rendering — old traces from before the
  // engine started propagating diagnostics won't have them.
  const hasDiag = socTrace.points.some(p => p?.diagnostics);
  if (!hasDiag) return null;

  return (
    <>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          width: '100%',
          background: 'var(--bg-card)',
          border: '0.5px solid var(--border-subtle)',
          borderRadius: 10,
          padding: '10px 14px',
          marginTop: 12,
          marginBottom: open ? 8 : 0,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          cursor: 'pointer',
          color: 'var(--text-secondary)',
          fontSize: 12,
        }}
        aria-expanded={open}
      >
        <span>Engine diagnostics ({socTrace.points.length} segments)</span>
        <span style={{ color: 'var(--text-tertiary)' }}>{open ? '▾' : '▸'}</span>
      </button>

      {open && (
        <div style={{
          background: 'var(--bg-card)',
          border: '0.5px solid var(--border-subtle)',
          borderRadius: 10,
          padding: 12,
          marginBottom: 12,
          fontSize: 11,
          fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace',
          color: 'var(--text-secondary)',
          overflow: 'auto',
          maxHeight: 400,
        }}>
          {/* Top-level summary */}
          <div style={{ marginBottom: 8, paddingBottom: 8, borderBottom: '0.5px solid var(--border-subtle)' }}>
            <span style={{ color: 'var(--accent-primary)' }}>mode:</span> {socTrace.mode}
            {' · '}
            <span style={{ color: 'var(--accent-primary)' }}>usable_kwh:</span> {socTrace.usable_kwh?.toFixed(1)}
            {' · '}
            <span style={{ color: 'var(--accent-primary)' }}>confidence:</span> ±{socTrace.confidence}%
          </div>

          {/* Per-segment table */}
          {socTrace.points.map((p, i) => (
            <SegmentRow key={i} index={i} point={p} />
          ))}
        </div>
      )}
    </>
  );
}

function SegmentRow({ index, point: p }) {
  const d = p.diagnostics || {};
  const dca = d.drive_cycle_audit || {};
  const ta = d.tire_audit || {};

  return (
    <div style={{
      paddingBottom: 8,
      marginBottom: 8,
      borderBottom: '0.5px solid var(--border-subtle)',
    }}>
      <div style={{ color: 'var(--text-primary)', marginBottom: 4 }}>
        seg #{index + 1}: {p.distance_km?.toFixed(1)} km @ {p.avg_speed_kmh?.toFixed(0)} km/h
        {' · '}
        <span style={{ color: 'var(--accent-primary)' }}>
          {p.soc_pct?.toFixed(1)}%
        </span>
      </div>
      <div style={{ paddingLeft: 8, lineHeight: 1.6 }}>
        <Pair k="ambient" v={fmtC(p.ambient_c)} />
        <Pair k="wind" v={fmtWind(p.wind_speed_kmh, p.wind_direction_deg)} />
        <Pair k="gradient" v={p.gradient_pct?.toFixed(2) + '%'} />
        <Pair k="energy" v={`${(p.segment_energy_wh / 1000).toFixed(2)} kWh (drive ${(p.drive_wh / 1000).toFixed(2)}, hvac ${(p.hvac_wh / 1000).toFixed(2)})`} />

        {d.drive_cycle_mult != null && (
          <Pair
            k="drive cycle"
            v={`×${d.drive_cycle_mult.toFixed(3)} (speed×${dca.speed_factor ?? '?'}, locale×${dca.locale_factor ?? '?'})`}
            tag={dca.source}
          />
        )}
        {d.tire_mult != null && (
          <Pair
            k="tire mult"
            v={`×${d.tire_mult.toFixed(3)}`}
            tag={ta.engine_version ?? (ta.model || 'v1')}
          />
        )}
        {d.road_mult != null && d.road_mult !== 1.0 && (
          <Pair k="road mult" v={`×${d.road_mult.toFixed(3)}`} />
        )}
        {d.eff_drivetrain != null && (
          <Pair k="drivetrain eff" v={d.eff_drivetrain.toFixed(3)} />
        )}
      </div>
    </div>
  );
}

function Pair({ k, v, tag }) {
  return (
    <div>
      <span style={{ color: 'var(--text-tertiary)' }}>{k}:</span>{' '}
      <span>{v}</span>
      {tag && (
        <span style={{
          marginLeft: 6, fontSize: 10,
          background: 'rgba(180, 140, 255, 0.1)',
          color: 'var(--accent-purple)',
          padding: '1px 5px', borderRadius: 4,
        }}>{tag}</span>
      )}
    </div>
  );
}

function fmtC(c) {
  return c == null ? '—' : `${c.toFixed(1)}°C`;
}
function fmtWind(speed, dir) {
  if (speed == null) return '—';
  return `${speed.toFixed(0)} km/h${dir != null ? ` from ${dir.toFixed(0)}°` : ''}`;
}
